import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Camera, X, Plus, Trash2, ChevronUp, ChevronDown, Image as ImageIcon, FlaskConical } from "lucide-react";
import { toast } from "sonner";

const DETECTION_TECHNIQUES = [
  "Aiguille détectable",
  "Caméra d'inspection",
  "Caméra endoscopique",
  "Caméra thermique",
  "Colorant et lampe UV",
  "Fumigène",
  "Gaz traceur",
  "Humidimètre",
  "Inspection visuelle",
  "Test d'aspersion en eau",
  "Test de pression",
  "Écoute acoustique",
];

const InvestigationSteps = ({ investigations = [], onChange }) => {
  const addStep = () => {
    const newStep = {
      order: investigations.length + 1,
      technique: "",
      description: "",
      photos: [],
      photo_captions: [],
      result: "",
    };
    onChange([...investigations, newStep]);
  };

  const removeStep = (index) => {
    const updated = investigations
      .filter((_, i) => i !== index)
      .map((step, i) => ({ ...step, order: i + 1 }));
    onChange(updated);
    toast.success("Étape supprimée");
  };

  const updateStep = (index, field, value) => {
    const updated = [...investigations];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const updateStepMulti = (index, fields) => {
    const updated = [...investigations];
    updated[index] = { ...updated[index], ...fields };
    onChange(updated);
  };

  const moveStep = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= investigations.length) return;
    const updated = [...investigations];
    [updated[index], updated[target]] = [updated[target], updated[index]];
    // Re-assign order numbers to match position
    const reordered = updated.map((step, i) => ({ ...step, order: i + 1 }));
    onChange(reordered);
  };

  return (
    <div className="space-y-4" data-testid="investigation-steps">
      <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg">
        <p className="text-sm font-medium text-sky-800">Étapes d'investigation</p>
        <p className="text-xs text-sky-600 mt-1">
          Ajoutez vos étapes dans l'ordre chronologique de l'investigation
        </p>
      </div>

      {investigations.length === 0 && (
        <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
          <FlaskConical className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm font-medium">Aucune étape d'investigation</p>
          <p className="text-slate-400 text-xs mt-1">Ajoutez votre première étape ci-dessous</p>
        </div>
      )}

      {investigations.map((step, index) => (
        <StepCard
          key={index}
          step={step}
          index={index}
          total={investigations.length}
          onUpdate={(field, value) => updateStep(index, field, value)}
          onUpdateMulti={(fields) => updateStepMulti(index, fields)}
          onRemove={() => removeStep(index)}
          onMoveUp={() => moveStep(index, -1)}
          onMoveDown={() => moveStep(index, 1)}
        />
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={addStep}
        className="w-full h-12 border-dashed border-2 border-sky-300 text-sky-700 hover:bg-sky-50 hover:border-sky-400 font-semibold"
        data-testid="add-investigation-step-btn"
      >
        <Plus className="w-5 h-5 mr-2" />
        Ajouter une étape
      </Button>
    </div>
  );
};

const StepCard = ({ step, index, total, onUpdate, onUpdateMulti, onRemove, onMoveUp, onMoveDown }) => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const processFiles = async (files) => {
    if (files.length === 0) return;
    const maxPhotos = 10;
    const currentPhotos = step.photos || [];
    if (currentPhotos.length + files.length > maxPhotos) {
      toast.error(`Maximum ${maxPhotos} photos par étape`);
      return;
    }
    setIsProcessing(true);
    try {
      const newPhotos = await Promise.all(
        files.map((file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              const img = new window.Image();
              img.onload = () => {
                const canvas = document.createElement("canvas");
                const maxSize = 1200;
                let width = img.width;
                let height = img.height;
                if (width > height && width > maxSize) {
                  height = (height * maxSize) / width;
                  width = maxSize;
                } else if (height > maxSize) {
                  width = (width * maxSize) / height;
                  height = maxSize;
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL("image/jpeg", 0.8));
              };
              img.onerror = reject;
              img.src = e.target.result;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );
      const currentCaptions = step.photo_captions || [];
      onUpdateMulti({
        photos: [...currentPhotos, ...newPhotos],
        photo_captions: [...currentCaptions, ...newPhotos.map(() => "")],
      });
      toast.success(`${newPhotos.length} photo(s) ajoutée(s)`);
    } catch (error) {
      toast.error("Erreur lors du traitement des photos");
    } finally {
      setIsProcessing(false);
    }
  };

  const removePhoto = (photoIndex) => {
    const newPhotos = (step.photos || []).filter((_, i) => i !== photoIndex);
    const newCaptions = (step.photo_captions || []).filter((_, i) => i !== photoIndex);
    onUpdateMulti({ photos: newPhotos, photo_captions: newCaptions });
  };

  const updateCaption = (photoIndex, value) => {
    const newCaptions = [...(step.photo_captions || [])];
    newCaptions[photoIndex] = value;
    onUpdate("photo_captions", newCaptions);
  };

  const photos = step.photos || [];
  const captions = step.photo_captions || [];

  return (
    <div
      className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden"
      data-testid={`investigation-step-${index}`}
    >
      {/* Step Header */}
      <div className="flex items-center justify-between bg-slate-50 px-4 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-sky-600 text-white text-sm font-bold">
            {step.order}
          </span>
          <span className="font-semibold text-slate-800 text-sm">
            INVESTIGATION N&deg;{step.order}{step.technique ? ` — ${step.technique}` : ""}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onMoveUp}
            disabled={index === 0}
            className="h-8 w-8"
            data-testid={`move-up-step-${index}`}
          >
            <ChevronUp className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="h-8 w-8"
            data-testid={`move-down-step-${index}`}
          >
            <ChevronDown className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
            data-testid={`remove-step-${index}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Step Content */}
      <div className="p-4 space-y-4">
        {/* Technique */}
        <div>
          <Label className="text-sm">Technique utilisée</Label>
          <Select
            value={step.technique}
            onValueChange={(v) => onUpdate("technique", v)}
          >
            <SelectTrigger className="h-12 mt-1" data-testid={`select-technique-${index}`}>
              <SelectValue placeholder="Sélectionner une technique..." />
            </SelectTrigger>
            <SelectContent>
              {DETECTION_TECHNIQUES.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Description */}
        <div>
          <Label className="text-sm">Description de l'étape</Label>
          <Textarea
            value={step.description}
            onChange={(e) => onUpdate("description", e.target.value)}
            placeholder="Décrivez ce qui a été fait, observé, constaté..."
            className="mt-1 min-h-[80px]"
            data-testid={`textarea-description-${index}`}
          />
        </div>

        {/* Result (optional) */}
        <div>
          <Label className="text-sm">Résultat / Conclusion de l'étape <span className="text-slate-400 font-normal">(optionnel)</span></Label>
          <Textarea
            value={step.result || ""}
            onChange={(e) => onUpdate("result", e.target.value)}
            placeholder="Résultat observé pour cette étape..."
            className="mt-1 min-h-[60px]"
            data-testid={`textarea-result-${index}`}
          />
        </div>

        {/* Photos */}
        <div>
          <Label className="text-sm mb-2 block">Photos de cette étape ({photos.length}/10)</Label>
          <div className="flex gap-2 mb-3">
            <Button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              disabled={isProcessing || photos.length >= 10}
              className="flex-1 h-11 bg-sky-600 hover:bg-sky-700 text-white text-sm"
              data-testid={`camera-btn-step-${index}`}
            >
              {isProcessing ? (
                <div className="spinner w-5 h-5 border-white border-t-transparent" />
              ) : (
                <>
                  <Camera className="w-4 h-4 mr-2" />
                  Photo
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing || photos.length >= 10}
              className="h-11 px-3 text-sm"
              data-testid={`gallery-btn-step-${index}`}
            >
              <Plus className="w-4 h-4 mr-1" />
              Galerie
            </Button>
          </div>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={async (e) => { await processFiles(Array.from(e.target.files)); e.target.value = ""; }}
            className="hidden"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={async (e) => { await processFiles(Array.from(e.target.files)); e.target.value = ""; }}
            className="hidden"
          />

          {photos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {photos.map((photo, pi) => (
                <div key={pi} className="space-y-1.5">
                  <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
                    <img src={photo} alt={`Photo ${pi + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(pi)}
                      className="absolute top-1.5 right-1.5 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600"
                      data-testid={`remove-photo-step-${index}-${pi}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <span className="absolute bottom-1.5 left-1.5 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded">
                      {pi + 1}
                    </span>
                  </div>
                  <input
                    type="text"
                    value={captions[pi] || ""}
                    onChange={(e) => updateCaption(pi, e.target.value)}
                    placeholder="Légende..."
                    className="w-full text-xs px-2 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-400"
                    data-testid={`caption-step-${index}-photo-${pi}`}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-dashed border-slate-200 rounded-lg p-4 text-center">
              <ImageIcon className="w-6 h-6 mx-auto text-slate-300 mb-1" />
              <p className="text-slate-400 text-xs">Aucune photo pour cette étape</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestigationSteps;
